import React, { useEffect, useMemo, useState } from 'react';
import { getPriceStockCalendar, getPriceInfoForReservation } from '../services/apiService';
import { getUserInfo } from '../utils/tokenManager';
import { getHotelBasicInfo, getProductDetails } from '../services/productService';

// Helper to normalize various table keys
const tryGet = (row, keys) => keys.reduce((acc, k) => (acc ?? row?.[k]), undefined);
const formatYMD = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// 가격 문자열에서 첫 번째 금액을 숫자로 추출 (예: "￦1,299,000 / ..." -> 1299000)
const extractFirstAmount = (raw) => {
  if (raw === null || raw === undefined) return null;
  const str = String(raw);
  const firstPart = str.split('/')[0];
  const digits = firstPart.replace(/[^0-9]/g, '');
  if (!digits) return null;
  const num = Number(digits);
  return Number.isFinite(num) ? num : null;
};

// 숫자 금액을 "만" 단위 소수점 한 자리로 포맷 (예: 1499000 -> "149.9만")
const formatToMan = (amount) => {
  if (amount === null || amount === undefined) return '';
  const man = amount / 10000;
  return `${man.toFixed(1)}만`;
};

function parseCalendarRows(resp) {
  const list = Array.isArray(resp?.table)
    ? resp.table
    : (Array.isArray(resp?.Table) ? resp.Table : (Array.isArray(resp?.Table1) ? resp.Table1 : []));
  const items = [];
  for (const row of (list || [])) {
    const dateStr = String(tryGet(row, ['가격일자','일자','날짜','판매일자','행사시작일시','Date','DATE']) || '').slice(0,10);
    if (!dateStr) continue;
    const priceNo = tryGet(row, ['가격번호','datePriceNo','가격ID','PriceNo']);
    const totalPriceStr = tryGet(row, ['총판매가','TotalPrice','totalPrice']);
    const status = tryGet(row, ['상태','Status','status']);
    const closed = String(status || '').includes('마감');
    const stockRaw = tryGet(row, ['보유석','잔여석','재고','AvailableStock','Stock']);
    let stock = 0;
    if (stockRaw !== undefined && stockRaw !== null && String(stockRaw).trim() !== '') {
      const num = Number(String(stockRaw).replace(/[^0-9.-]/g, ''));
      if (Number.isFinite(num)) stock = num;
    }
    // 항공패턴 (재고구분)
    const flightPattern = tryGet(row, ['항공패턴','항공','패턴명','pattern','자원명','ResourceName']) || '';
    // 요금제
    const ratePlan = tryGet(row, ['요금제','가격명','pricePlan','PlanName','가격옵션명']) || '';
    items.push({ date: dateStr, priceNo, totalPriceStr: totalPriceStr || '', closed, flightPattern, ratePlan, stock });
  }
  // sort by date asc
  items.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  // dedupe by date+priceNo (some APIs can return duplicates)
  const seen = new Set();
  return items.filter((it) => {
    const key = `${it.date}-${it.priceNo ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const AvailableDatesModal = ({ open, onClose, productNo, productName, onSelect, initialProductTypeName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]); // [{date, priceNo, totalPriceStr, closed, flightPattern, ratePlan}]
  const [productTypeName, setProductTypeName] = useState(''); // 호텔 | 투어 (detect before load)
  const [selectedPattern, setSelectedPattern] = useState('');
  const [selectedRatePlan, setSelectedRatePlan] = useState('');
  const [range, setRange] = useState(() => {
    const s = new Date();
    const e = new Date();
    e.setFullYear(e.getFullYear() + 1);
    return { start: formatYMD(s), end: formatYMD(e) };
  });
  const [currentMonth, setCurrentMonth] = useState(() => {
    const s = new Date();
    return { year: s.getFullYear(), month: s.getMonth() };
  });

  const filteredItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    return items.filter((it) => {
      if (!it || !it.date) return false;
      if (selectedPattern && it.flightPattern !== selectedPattern) return false;
      if (selectedRatePlan && it.ratePlan !== selectedRatePlan) return false;
      return true;
    });
  }, [items, selectedPattern, selectedRatePlan]);

  const itemsByDate = useMemo(() => {
    const map = new Map();
    for (const it of filteredItems || []) {
      if (!it?.date) continue;
      if (!map.has(it.date)) map.set(it.date, []);
      map.get(it.date).push(it);
    }
    return map;
  }, [filteredItems]);

  const monthCells = useMemo(() => {
    const year = currentMonth.year;
    const month = currentMonth.month;
    const firstDayDate = new Date(year, month, 1);
    const firstWeekday = firstDayDate.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    let dayCounter = 1 - firstWeekday;
    for (let i = 0; i < 42; i += 1) {
      const dateObj = new Date(year, month, dayCounter);
      const inMonth = dateObj.getMonth() === month;
      const dateStr = inMonth ? formatYMD(dateObj) : '';
      const dailyItems = inMonth && dateStr ? (itemsByDate.get(dateStr) || []) : [];
      cells.push({ index: i, inMonth, dateStr, items: dailyItems, dateObj });
      dayCounter += 1;
    }
    return cells;
  }, [currentMonth, itemsByDate]);

  const patternOptions = useMemo(() => {
    const set = new Set();
    for (const it of items || []) {
      if (it?.flightPattern) set.add(it.flightPattern);
    }
    return Array.from(set);
  }, [items]);

  const ratePlanOptions = useMemo(() => {
    const set = new Set();
    for (const it of items || []) {
      if (it?.ratePlan) set.add(it.ratePlan);
    }
    return Array.from(set);
  }, [items]);

  // 데이터가 로딩되면 항공패턴/요금제는 항상 하나가 선택되도록 기본값 설정
  useEffect(() => {
    if (!open) return;
    if (!items || items.length === 0) return;
    if (!selectedPattern && patternOptions.length > 0) {
      setSelectedPattern(patternOptions[0]);
    }
    if (!selectedRatePlan && ratePlanOptions.length > 0) {
      setSelectedRatePlan(ratePlanOptions[0]);
    }
  }, [open, items, patternOptions, ratePlanOptions, selectedPattern, selectedRatePlan]);

  const { secondMonthCells, secondMonthLabel } = useMemo(() => {
    const baseYear = currentMonth.year;
    const baseMonth = currentMonth.month;
    const next = new Date(baseYear, baseMonth + 1, 1);
    const year = next.getFullYear();
    const month = next.getMonth();
    const firstDayDate = new Date(year, month, 1);
    const firstWeekday = firstDayDate.getDay();
    const cells = [];
    let dayCounter = 1 - firstWeekday;
    for (let i = 0; i < 42; i += 1) {
      const dateObj = new Date(year, month, dayCounter);
      const inMonth = dateObj.getMonth() === month;
      const dateStr = inMonth ? formatYMD(dateObj) : '';
      const dailyItems = inMonth && dateStr ? (itemsByDate.get(dateStr) || []) : [];
      cells.push({ index: i, inMonth, dateStr, items: dailyItems, dateObj });
      dayCounter += 1;
    }
    return {
      secondMonthCells: cells,
      secondMonthLabel: `${year}년 ${month + 1}월`,
    };
  }, [currentMonth, itemsByDate]);

  // Determine product type once when modal opens or product changes
  useEffect(() => {
    let active = true;
    // If parent provided an override, use it immediately
    if (initialProductTypeName) {
      setProductTypeName(String(initialProductTypeName).trim());
    }
    const detectType = async () => {
      if (!open || !productNo) return;
      if (initialProductTypeName) return; // skip detection when provided by parent
      try {
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) return;
        // 1) Prefer general product details with type code/name
        let typeName = '투어';
        const details = await getProductDetails(productNo, companyNo).catch(() => null);
        if (details) {
          const rawType = String(details.상품타입 || details.ProductType || '').trim();
          const rawTypeCode = String(details.상품종류코드 || details.ProductTypeCode || '').trim();
          const rawTypeCode2 = String(details.상품타입코드 || details.ProductTypeCode || '').trim();
          const hotelTypeCodes = new Set(['309001','309002','309003','309004','309005','309006','309007','309008']);
          if (
            rawType.includes('호텔') ||
            rawTypeCode === '001001' ||
            hotelTypeCodes.has(rawTypeCode2)
          ) {
            typeName = '호텔';
          } else {
            typeName = '투어';
          }
        } else {
          // 2) Fallback: probe hotel basic info
          const hotelInfo = await getHotelBasicInfo(productNo, companyNo).catch(() => null);
          typeName = hotelInfo ? '호텔' : '투어';
        }
        if (!active) return;
        setProductTypeName(typeName);
      } catch {
        if (!active) return;
        setProductTypeName('투어');
      }
    };
    detectType();
    return () => { active = false; };
  }, [open, productNo]);

  useEffect(() => {
    if (!items || items.length === 0) return;
    const firstDate = items[0]?.date;
    if (!firstDate) return;
    const y = Number(String(firstDate).slice(0, 4));
    const m = Number(String(firstDate).slice(5, 7)) - 1;
    if (!Number.isFinite(y) || !Number.isFinite(m)) return;
    setCurrentMonth((prev) => {
      if (prev.year === y && prev.month === m) return prev;
      return { year: y, month: m };
    });
  }, [items]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!open) return;
      if (!productNo) return;
      if (!productTypeName) return; // wait until type detected to avoid defaulting to '투어'
      try {
        setLoading(true);
        setError('');
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) {
          setError('업체 정보가 없습니다.');
          setItems([]);
          return;
        }
        const resp = await getPriceStockCalendar({
          productTypeName,
          productNo: Number(productNo),
          companyNo: String(companyNo),
          startDate: String(range.start).slice(0,10),
          endDate: String(range.end).slice(0,10),
        }).catch(() => null);
        if (!active) return;
        const parsed = parseCalendarRows(resp);
        setItems(parsed);
      } catch (e) {
        if (!active) return;
        console.error('Failed to load available dates', e);
        setError('예약 가능한 날짜를 불러오는 중 오류가 발생했습니다.');
        setItems([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [open, productNo, range.start, range.end, productTypeName]);

  if (!open) return null;

  const monthLabel = `${currentMonth.year}년 ${currentMonth.month + 1}월`;

  const goToPrevMonth = () => {
    setCurrentMonth((prev) => {
      const isJan = prev.month === 0;
      const newYear = isJan ? prev.year - 1 : prev.year;
      const newMonth = isJan ? 11 : prev.month - 1;
      return { year: newYear, month: newMonth };
    });
  };

  const goToNextMonth = () => {
    setCurrentMonth((prev) => {
      const isDec = prev.month === 11;
      const newYear = isDec ? prev.year + 1 : prev.year;
      const newMonth = isDec ? 0 : prev.month + 1;
      return { year: newYear, month: newMonth };
    });
  };

  const handlePick = async (it) => {
    try {
      const { companyNo } = getUserInfo() || {};
      if (!companyNo) { alert('업체 정보가 없습니다.'); return; }
      // Verify availability with a light reservation price info probe (same as PriceStockSettings flow)
      const effectivePriceNo = Number.isFinite(Number(it.priceNo)) ? Number(it.priceNo) : 0;
      const params = {
        companyNo,
        priceNo: Number(effectivePriceNo),
        nights: 1,
        adultCount: 1,
        ageArray: '',
        roomCount: 1,
        productTypeName,
      };
      const response = await getPriceInfoForReservation(params).catch(() => null);
      let roomsLen = 0;
      if (response && Array.isArray(response.table) && response.table.length > 0) {
        const infoStr = response.table[0]?.정보;
        try {
          const parsed = typeof infoStr === 'string' ? JSON.parse(infoStr) : (infoStr || {});
          const arr = Array.isArray(parsed?.객실상품) ? parsed.객실상품 : [];
          roomsLen = arr.length;
        } catch (_) {
          roomsLen = 0;
        }
      }
      if (roomsLen <= 0) {
        alert('재고가 없거나 마감되어 예약이 불가능합니다.');
        return;
      }
      onSelect && onSelect({ date: it.date, datePriceNo: String(effectivePriceNo), productTypeName });
    } catch (e) {
      console.error('예약 가능 여부 확인 실패', e);
      alert('예약 가능 여부 확인 중 오류가 발생했습니다.');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white w-full max-w-6xl max-h-[90vh] flex flex-col rounded-xl p-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-bold text-gray-900">예약 가능 날짜 선택</h3>
            <div className="ml-auto text-gray-600">
            {productName ? <span>{productName}</span> : null}
          </div>
          </div>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose}>✕</button>
        </div>

        {/* Range controls */}
        <div className="px-6 py-3 border-b bg-gray-50 flex flex-wrap items-center gap-3 text-sm">
          <div className="flex items-center gap-2">
            <label className="text-gray-600">시작</label>
            <input
              type="date"
              value={range.start}
              onChange={(e) => setRange((p) => ({ ...p, start: e.target.value }))}
              className="border rounded px-2 py-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-gray-600">종료</label>
            <input
              type="date"
              value={range.end}
              onChange={(e) => setRange((p) => ({ ...p, end: e.target.value }))}
              className="border rounded px-2 py-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-gray-600">항공패턴</label>
            <select
              value={selectedPattern}
              onChange={(e) => setSelectedPattern(e.target.value)}
              className="border rounded px-2 py-1 bg-white"
            >
              {patternOptions.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-gray-600">요금제</label>
            <select
              value={selectedRatePlan}
              onChange={(e) => setSelectedRatePlan(e.target.value)}
              className="border rounded px-2 py-1 bg-white"
            >
              {ratePlanOptions.map((rp) => (
                <option key={rp} value={rp}>{rp}</option>
              ))}
            </select>
          </div>
          
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 px-6 py-3 overflow-y-auto">
          {loading && (
            <div className="py-10 text-center text-gray-500">불러오는 중...</div>
          )}
          {!loading && error && (
            <div className="py-4 text-center text-red-600 text-sm">{error}</div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="py-10 text-center text-gray-500 text-sm">예약 가능한 날짜가 없습니다.</div>
          )}
          {!loading && !error && items.length > 0 && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-center text-sm">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                    onClick={goToPrevMonth}
                  >
                    {'<'}
                  </button>
                  <span className="font-medium text-gray-800">
                    {monthLabel} ~ {secondMonthLabel}
                  </span>
                  <button
                    type="button"
                    className="px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                    onClick={goToNextMonth}
                  >
                    {'>'}
                  </button>
                </div>
                
              </div>

              <div className="flex gap-4">
                {/* 첫 번째 달 */}
                <div className="flex-1">
                  <div className="mb-1 text-[16px] font-medium text-gray-700 text-center">{monthLabel}</div>
                  <div className="grid grid-cols-7 text-xs text-center text-gray-500 mb-1">
                    <div className="text-red-400">일</div>
                    <div>월</div>
                    <div>화</div>
                    <div>수</div>
                    <div>목</div>
                    <div>금</div>
                    <div className="text-blue-500">토</div>
                  </div>
                  <div className="grid grid-cols-7 gap-px bg-gray-200 rounded overflow-hidden text-xs">
                    {monthCells.map((cell) => {
                      if (!cell.inMonth) {
                        return <div key={cell.index} className="bg-gray-50 h-20" />;
                      }
                      const hasItems = Array.isArray(cell.items) && cell.items.length > 0;
                      const primary = hasItems ? cell.items[0] : null;
                      const isClosed = primary?.closed;
                      const amount = primary?.totalPriceStr != null ? extractFirstAmount(primary.totalPriceStr) : null;
                      const stock = typeof primary?.stock === 'number' ? primary.stock : 0;
                      const hasPrice = amount != null;
                      const hasStock = stock > 0;
                      const isReservable = hasItems && !isClosed && hasPrice && hasStock;
                      const priceText = amount != null ? formatToMan(amount) : '';
                      const weekday = cell.dateObj.getDay();
                      const dayColor = !hasItems
                        ? 'text-gray-400'
                        : (weekday === 0
                          ? 'text-red-500'
                          : weekday === 6
                            ? 'text-blue-500'
                            : 'text-gray-800');
                      return (
                        <button
                          key={cell.index}
                          type="button"
                          disabled={!isReservable}
                          onClick={() => isReservable && primary && handlePick(primary)}
                          className={`h-20 w-full flex flex-col items-stretch justify-between p-1.5 text-left bg-white ${
                            !isReservable ? 'bg-gray-50 text-gray-300' : ''
                          } ${isClosed ? 'opacity-60' : ''}`}
                        >
                          <div className="flex flex-col h-full">
                            <div className={`text-[11px] font-medium ${dayColor}`}>
                              {cell.dateObj.getDate()}
                            </div>
                            <div className="mt-1 text-[11px] text-gray-700 truncate">
                              {hasItems ? priceText : null}
                            </div>
                            <div className="mt-auto pt-1">
                              {isReservable && (
                                <span className="inline-flex w-full justify-center px-0.5 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-medium">
                                  예약
                                </span>
                              )}
                              {!isReservable && hasItems && isClosed && (
                                <span className="inline-flex w-full justify-center px-1.5 py-0.5 rounded-full bg-orange-400 text-white text-[10px] font-medium">
                                  마감
                                </span>
                              )}
                              {hasItems && !isClosed && (!hasPrice || !hasStock) && (
                                <span className="inline-flex w-full justify-center px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-300 text-[10px] font-medium">
                                  예약불가
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 두 번째 달 */}
                <div className="flex-1">
                  <div className="mb-1 text-[16px] font-medium text-gray-700 text-center">{secondMonthLabel}</div>
                  <div className="grid grid-cols-7 text-xs text-center text-gray-500 mb-1">
                    <div className="text-red-400">일</div>
                    <div>월</div>
                    <div>화</div>
                    <div>수</div>
                    <div>목</div>
                    <div>금</div>
                    <div className="text-blue-500">토</div>
                  </div>
                  <div className="grid grid-cols-7 gap-px bg-gray-200 rounded overflow-hidden text-xs">
                    {secondMonthCells.map((cell) => {
                      if (!cell.inMonth) {
                        return <div key={cell.index} className="bg-gray-50 h-20" />;
                      }
                      const hasItems = Array.isArray(cell.items) && cell.items.length > 0;
                      const primary = hasItems ? cell.items[0] : null;
                      const isClosed = primary?.closed;
                      const amount = primary?.totalPriceStr != null ? extractFirstAmount(primary.totalPriceStr) : null;
                      const stock = typeof primary?.stock === 'number' ? primary.stock : 0;
                      const hasPrice = amount != null;
                      const hasStock = stock > 0;
                      const isReservable = hasItems && !isClosed && hasPrice && hasStock;
                      const priceText = amount != null ? formatToMan(amount) : '';
                      const weekday = cell.dateObj.getDay();
                      const dayColor = !hasItems
                        ? 'text-gray-400'
                        : (weekday === 0
                          ? 'text-red-500'
                          : weekday === 6
                            ? 'text-blue-500'
                            : 'text-gray-800');
                      return (
                        <button
                          key={cell.index}
                          type="button"
                          disabled={!isReservable}
                          onClick={() => isReservable && primary && handlePick(primary)}
                          className={`h-20 w-full flex flex-col items-stretch justify-between p-1.5 text-left bg-white ${
                            !isReservable ? 'bg-gray-50 text-gray-300' : ''
                          } ${isClosed ? 'opacity-60' : ''}`}
                        >
                          <div className="flex flex-col h-full">
                            <div className={`text-[11px] font-medium ${dayColor}`}>
                              {cell.dateObj.getDate()}
                            </div>
                            <div className="mt-1 text-[11px] text-gray-700 truncate">
                              {hasItems ? priceText : null}
                            </div>
                            <div className="mt-auto pt-1">
                              {isReservable && (
                                <span className="inline-flex w-full justify-center px-1 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-medium">
                                  예약
                                </span>
                              )}
                              {!isReservable && hasItems && isClosed && (
                                <span className="inline-flex w-full justify-center px-1.5 py-0.5 rounded-full bg-orange-400 text-white text-[10px] font-medium">
                                  마감
                                </span>
                              )}
                              {hasItems && !isClosed && (!hasPrice || !hasStock) && (
                                <span className="inline-flex w-full justify-center px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-300 text-[10px] font-medium">
                                  예약불가
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      
      </div>
    </div>
  );
};

export default AvailableDatesModal;
